import React, { FC, useState } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import { faExclamationCircle } from '@fortawesome/free-solid-svg-icons/faExclamationCircle';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useController, useFormContext } from 'react-hook-form';
import classNames from 'classnames';

import Button, { BUTTON_SIZES, BUTTON_TYPES } from 'buyside/components/presentation/Button/button';

import { withThesisImageConfig } from 'buyside/util/tinymceConfig';

import { useCreateThesisImageMutation } from 'interfaces/graphql-typings';

import fieldStyles from 'buyside/components/presentation/Fields/fieldStyles.scss';
import styles from './sharedFieldsStyles.scss';

const ThesisField: FC<{
  imageableId: string;
  imageableType: 'Idea' | 'WhitePaper';
  onAttachThesisInstead: () => void;
}> = ({ imageableId, imageableType, onAttachThesisInstead }) => {
  const [isInitialized, setIsInitialized] = useState(false);

  const { setValue } = useFormContext();

  const {
    field: { name, onBlur, ref, value },
    fieldState: { error },
    formState: { isSubmitting }
  } = useController({ name: 'thesis' });

  const [handleCreateThesisImage] = useCreateThesisImageMutation();

  return (
    <div
      id={`thesis-editor${isInitialized ? '-initialized' : ''}`}
      className={classNames(fieldStyles.field, fieldStyles.medium)}
    >
      <label data-title>Thesis</label>
      <div
        className={classNames(styles.editorWrapper, { [styles.error]: Boolean(error) })}
        ref={ref}
        tabIndex={-1}
      >
        <Editor
          id={name}
          disabled={isSubmitting || !isInitialized}
          init={withThesisImageConfig({
            imageableId,
            createThesisImage: ({ imageableId, image }) =>
              handleCreateThesisImage({
                variables: {
                  image,
                  imageableId,
                  type: imageableType
                }
              })
          })}
          onBlur={onBlur}
          onEditorChange={value => {
            if (isInitialized)
              setValue('thesis', value, {
                shouldDirty: true,
                shouldTouch: true,
                shouldValidate: true
              });
          }}
          onInit={() => setIsInitialized(true)}
          value={value}
        />
      </div>
      {error ? (
        <label data-error>
          <FontAwesomeIcon icon={faExclamationCircle} />
          {error.message}
        </label>
      ) : null}
      <Button
        className={styles.uploadThesisButton}
        onClick={() => {
          setValue('thesis', '', {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true
          });
          onAttachThesisInstead();
        }}
        size={BUTTON_SIZES.SMALL}
        text="Attach Your Thesis Instead"
        type={BUTTON_TYPES.LINK}
      />
      <small>
        Plagiarism is taken seriously and may result in account deletion or other corrective
        actions. Please cite your sources.
      </small>
      <small>
        Images will be automatically resized to 800 pixels for the longer side.{' '}
        {imageableType === 'Idea' ? (
          <>
            Please upload a formatted thesis below or your materials as a supporting document if you
            need larger images.
          </>
        ) : (
          <>
            Please upload your materials as a supporting document below if you need larger images.
          </>
        )}
      </small>
    </div>
  );
};

export default ThesisField;
